// App-level building blocks on UI Core specs
// references/components.md): Badge, Notice, EmptyState, Skeleton, Field,
// PasswordInput, Switch, Dialog, Tabs, and the page header. Styles live in
// ../styles.css; every value there is a token.
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import type { DocStatus } from "../api";
import { STATUS_LABEL } from "../lib/labels";
import { PRODUCT_NAME } from "../lib/product";
import { Glyph, type GlyphName } from "./icons";

// ---------------------------------------------------------------------------
// Badge (text always present; status tint only for real state)
// ---------------------------------------------------------------------------
export function StatusBadge({ status }: { status: DocStatus }) {
  return (
    <span className="badge" data-status={status}>
      {STATUS_LABEL[status]}
    </span>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "success" | "warning" | "danger" | "info"; children: ReactNode }) {
  return (
    <span className="badge" data-tone={tone}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Notice: icon + a title that names the state + tonal surface + full border
// ---------------------------------------------------------------------------
const NOTICE_ICON: Record<NoticeTone, GlyphName> = {
  info: "infoCircle",
  success: "tickCircle",
  warning: "warning",
  danger: "danger",
};

export type NoticeTone = "info" | "success" | "warning" | "danger";

export interface NoticeProps {
  tone: NoticeTone;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function Notice({ tone, title, children, action, className }: NoticeProps) {
  return (
    <div className={className ? `notice ${className}` : "notice"} data-tone={tone} role={tone === "danger" ? "alert" : "status"}>
      <span className="notice-icon">
        <Glyph name={NOTICE_ICON[tone]} />
      </span>
      <div className="notice-body">
        <p className="notice-title">{title}</p>
        {children ? <div className="notice-text">{children}</div> : null}
        {action ? <div className="notice-action">{action}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page header: one h1 plus one lead, actions at the end from 640px
// ---------------------------------------------------------------------------
export interface PageHeaderProps {
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  back?: { href: string; label: string };
  titleSize?: "page" | "document";
  /** A small caps label above the title (the template's "DOCUMENTS / GENERATOR"). */
  eyebrow?: ReactNode;
  /** A hairline under the header: the list screens share it above their toolbar. */
  divided?: boolean;
}

/**
 * The title the shell's phone header already shows (ChatShell's screen name),
 * or null. A page title equal to it is hidden on a phone (Fase 4: no H1 that
 * repeats the top bar); it stays the page's heading for screen readers.
 */
export const ShellTitleContext = createContext<string | null>(null);

/** The three page types (Fase 4): a list page (1200px, the default), a form page (760px), a workspace (full; the route's fill). */
export type PageLayout = "form";

export function PageHeader({ title, lead, actions, back, titleSize = "page", divided, eyebrow }: PageHeaderProps) {
  const shellTitle = useContext(ShellTitleContext);
  const repeatsShell = shellTitle !== null && typeof title === "string" && title === shellTitle;
  return (
    <header className={divided ? "page-head divided-below" : "page-head"} data-size={titleSize}>
      {back ? (
        <a className="back-link" href={back.href}>
          <Glyph name="arrowLeft" />
          <span>{back.label}</span>
        </a>
      ) : null}
      <div className="page-head-row">
        <div className="page-head-text">
          {eyebrow ? <p className="page-eyebrow">{eyebrow}</p> : null}
          <h1 className="page-title" data-shell-title={repeatsShell ? "" : undefined}>
            {title}
          </h1>
          {lead ? <p className="page-lead">{lead}</p> : null}
        </div>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Section head inside a region
// ---------------------------------------------------------------------------
export function SectionHead({ id, title, lead, action }: { id?: string; title: ReactNode; lead?: ReactNode; action?: ReactNode }) {
  return (
    <div className="section-head">
      <div className="section-head-text">
        <h2 id={id} className="section-title">
          {title}
        </h2>
        {lead ? <p className="section-lead">{lead}</p> : null}
      </div>
      {action ? <div className="section-action">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state: icon, title, one sentence, one action
// ---------------------------------------------------------------------------
export function EmptyState({ icon, title, body, action }: { icon: GlyphName; title: string; body: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Glyph name={icon} size={24} />
      </span>
      <p className="empty-title">{title}</p>
      <p className="empty-body">{body}</p>
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton rows, shown only after --wait-skeleton (300ms) so fast loads never flash
// ---------------------------------------------------------------------------
export function useDelayed(active: boolean, ms = 300): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  return shown;
}

export function SkeletonRows({ count = 4, label = "Memuat data" }: { count?: number; label?: string }) {
  const shown = useDelayed(true);
  return (
    <div className="skeleton-rows" aria-busy="true" aria-label={label} role="status">
      {shown
        ? Array.from({ length: count }, (_, i) => (
            <div className="skeleton-row" key={i} aria-hidden="true">
              <span className="sk sk-title" />
              <span className="sk sk-meta" />
            </div>
          ))
        : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field: label above, control, a reserved hint or error row below
// ---------------------------------------------------------------------------
export interface FieldProps {
  id: string;
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  wide?: boolean;
  children: ReactNode;
}

export function Field({ id, label, required, hint, error, wide, children }: FieldProps) {
  return (
    <div className="field" data-wide={wide ? "" : undefined}>
      <label htmlFor={id}>
        {label}
        {required ? (
          <span className="req" aria-hidden="true">
            {" "}
            *
          </span>
        ) : null}
      </label>
      {children}
      <p className="field-hint" id={`${id}-hint`} data-state={error ? "error" : undefined} aria-live={error ? "polite" : undefined}>
        {error ? (
          <>
            <Glyph name="danger" size={16} />
            <span>{error}</span>
          </>
        ) : (
          hint ?? null
        )}
      </p>
    </div>
  );
}

/** aria props a control inside <Field> takes. */
export function fieldAria(id: string, error?: string | null) {
  return { id, "aria-describedby": `${id}-hint`, "aria-invalid": error ? true : undefined } as const;
}

// ---------------------------------------------------------------------------
// Password input: the reveal is an icon button inside the field's trailing
// edge (design review ui.component_recipe core.field_trailing_action). The bordered box
// is the wrapper; the input and the 44px button sit in it as flex siblings,
// so nothing overlaps and the value never runs under the button. Hover,
// focus, invalid, and disabled are drawn on the box (styles.css .field-box).
// ---------------------------------------------------------------------------
export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="field-box" data-invalid={props["aria-invalid"] ? "" : undefined} data-disabled={props.disabled ? "" : undefined}>
      <input {...props} type={shown ? "text" : "password"} />
      <button
        type="button"
        className="field-box-action"
        aria-label={shown ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
        aria-pressed={shown}
        aria-controls={props.id}
        title={shown ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
        disabled={props.disabled}
        onClick={() => setShown((s) => !s)}
      >
        <Glyph name={shown ? "eyeSlash" : "eye"} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search input: the leading search icon sits in a reserved 40px lane
// ---------------------------------------------------------------------------
export function SearchInput({
  id,
  value,
  onChange,
  placeholder,
  label,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label?: string;
}) {
  return (
    <div className="control-wrap search-wrap">
      <Glyph name="searchNormal" />
      <input
        id={id}
        type="search"
        className="control-icon-start"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Switch (Material switch on the native checkbox, ui.css .ui-switch)
// ---------------------------------------------------------------------------
export function Switch({
  id,
  label,
  checked,
  disabled,
  onChange,
  describedBy,
  size,
}: {
  id: string;
  label: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  describedBy?: string;
  /** "sm": a 32 x 18 track for dense chrome (the sidebar foot); the row stays 44px. */
  size?: "sm";
}) {
  // The input lives inside the track it draws, never over the label text, so
  // no two siblings overlap; the label row is the 44px target.
  return (
    <label className="ui-switch" htmlFor={id} data-size={size}>
      <span className="ui-switch-track">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          aria-checked={checked}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      </span>
      <span className="ui-switch-label">{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Dialog: native showModal, scrim, bottom sheet below 640px
// ---------------------------------------------------------------------------
export interface DialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Blocks Escape and scrim dismissal while a request is running. */
  busy?: boolean;
}

export function Dialog({ open, title, description, onClose, children, footer, size = "md", busy }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
      // Focus the first field (data-autofocus) or the dialog itself, never
      // the close button, so a ring does not flash on the corner at open.
      const target = d.querySelector<HTMLElement>("[data-autofocus]") ?? d.querySelector<HTMLElement>(".dialog-frame");
      target?.focus?.();
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
      const back = opener.current as HTMLElement | null;
      back?.focus?.();
    }
  }, [open]);

  const onCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!busy) onClose();
  };

  return (
    <dialog
      ref={ref}
      className="dialog"
      role="dialog"
      aria-modal="true"
      data-size={size}
      aria-labelledby={titleId}
      onCancel={onCancel}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      {open ? (
      <div className="dialog-frame" tabIndex={-1}>
        <div className="dialog-head">
          <div className="dialog-head-text">
            <h2 id={titleId} className="dialog-title">
              {title}
            </h2>
            {description ? <p className="dialog-desc">{description}</p> : null}
          </div>
          <button type="button" className="ui-icon-btn dialog-close" aria-label="Tutup" onClick={() => !busy && onClose()} disabled={busy}>
            <Glyph name="close" />
          </button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer ? <div className="dialog-foot">{footer}</div> : null}
      </div>
      ) : null}
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Secondary tabs (manual activation, layer-2 selected fill, no underline)
// ---------------------------------------------------------------------------
export interface TabItem {
  id: string;
  label: string;
}

export function Tabs({ label, items, value, onChange, idBase }: { label: string; items: TabItem[]; value: string; onChange: (id: string) => void; idBase: string }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focus, setFocus] = useState(() => Math.max(0, items.findIndex((t) => t.id === value)));
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = i;
    if (e.key === "ArrowRight") next = (i + 1) % items.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    else return;
    e.preventDefault();
    setFocus(next);
    refs.current[next]?.focus();
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((t, i) => {
        const on = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${t.id}`}
            aria-controls={`${idBase}-panel`}
            aria-selected={on}
            tabIndex={i === focus ? 0 : -1}
            className="tab"
            onKeyDown={(e) => onKey(e, i)}
            onClick={() => {
              setFocus(i);
              onChange(t.id);
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Async data hook
// ---------------------------------------------------------------------------
export interface Async<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  set: (next: T) => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): Async<T> {
  const [state, setState] = useState<{ data: T | null; error: unknown; loading: boolean }>({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ data: s.data, error: null, loading: true }));
    load().then(
      (data) => alive && setState({ data, error: null, loading: false }),
      (error) => alive && setState({ data: null, error, loading: false }),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return {
    ...state,
    reload: () => setTick((t) => t + 1),
    set: (next: T) => setState({ data: next, error: null, loading: false }),
  };
}

/** Sets document.title as "<screen> | Public Procurement Tools (Pluto)". */
export function useTitle(title: string): void {
  useEffect(() => {
    document.title = title ? `${title} | ${PRODUCT_NAME}` : PRODUCT_NAME;
  }, [title]);
}
